import { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ListChecks, AlertTriangle, Camera } from 'lucide-react';
import { PageHeader, Card } from '@/components/ui/Card';
import Select from '@/components/ui/Select';
import TrackBar from '@/components/creative/TrackBar';
import { fetchTrack, fetchTrackSummary, fetchClients, qk, type TrackRow } from '@/lib/queries';
import { useTasksOnly } from '@/hooks/use-scopes';
import { cn } from '@/lib/cn';

/** Every open creative, grouped by what it is waiting on and who owns that step. */
export default function TrackerPage() {
  const [params, setParams] = useSearchParams();
  const next = params.get('next') ?? '';
  const [clientId, setClientId] = useState(params.get('client') ?? '');
  const [showDone, setShowDone] = useState(false);
  const [doctor, setDoctor] = useState('');
  const wide = useTasksOnly() === false;
  const clients = useQuery({ queryKey: qk.clients, queryFn: fetchClients, enabled: wide });
  const rows = useQuery({ queryKey: ['track', 'rows', clientId, showDone], queryFn: () => fetchTrack(clientId || undefined, showDone), refetchInterval: 30000 });
  const summary = useQuery({ queryKey: ['track', 'summary', clientId], queryFn: () => fetchTrackSummary(clientId || undefined), refetchInterval: 30000 });

  const groups = useMemo(() => {
    const m = new Map<string, TrackRow[]>();
    for (const r of rows.data ?? []) {
      if (next === 'overdue' ? !r.next?.overdue : (next && r.next?.key !== next)) continue;
      if (doctor && r.talent_name !== doctor) continue;
      const k = r.next?.key ?? 'done';
      if (!m.has(k)) m.set(k, []);
      m.get(k)!.push(r);
    }
    for (const list of m.values()) list.sort((a, b) => (b.next?.days ?? 0) - (a.next?.days ?? 0));
    return Array.from(m.entries()).sort((a, b) => b[1].length - a[1].length);
  }, [rows.data, next, doctor]);
  const doctors = useMemo(() => Array.from(new Set((rows.data ?? []).map((r) => r.talent_name).filter((x): x is string => !!x))).sort(), [rows.data]);
  const labels = summary.data?.labels ?? {};
  const byNext = Object.entries(summary.data?.by_next ?? {}).sort((a, b) => b[1] - a[1]);

  return (
    <div>
      <PageHeader title="Tracker" subtitle="Every open creative, grouped by what it is waiting on and who owns that step." />

      {summary.data && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
          <Tile label="Open creatives" value={summary.data.total_open} />
          <Tile label="Blocked" value={summary.data.blocked} tone={summary.data.blocked ? 'red' : undefined} />
          <Tile label="Over the SLA" value={summary.data.overdue} tone={summary.data.overdue ? 'red' : undefined} />
          <Tile label="Waiting for a doctor" value={summary.data.by_next.doctor ?? 0} hint={summary.data.waiting_doctor_oldest_days ? `oldest ${summary.data.waiting_doctor_oldest_days}d` : undefined} tone={summary.data.waiting_doctor_oldest_days > 2 ? 'amber' : undefined} />
          <Tile label="Shoots next 7 days" value={summary.data.shoots_next_7} hint={[summary.data.shoots_no_crew ? `${summary.data.shoots_no_crew} without crew` : '', summary.data.on_shoot_not_ready ? `${summary.data.on_shoot_not_ready} creatives not ready` : ''].filter(Boolean).join(', ') || undefined} tone={summary.data.shoots_no_crew || summary.data.on_shoot_not_ready ? 'amber' : undefined} icon={<Camera size={12} />} />
        </div>
      )}

      <div className="flex flex-wrap gap-2 mb-4">
        <button onClick={() => setParams({})} className={cn('text-xs px-3 py-1.5 min-h-[36px] rounded-full border transition', !next ? 'border-accent-400 bg-accent-600/20 text-accent-400' : 'border-slate-800 text-muted hover:text-muted-strong')}>All</button>
        {(summary.data?.overdue ?? 0) > 0 && <button onClick={() => setParams({ next: 'overdue' })} className={cn('text-xs px-3 py-1.5 min-h-[36px] rounded-full border transition', next === 'overdue' ? 'border-red-400 bg-red-500/15 text-red-300' : 'border-red-500/40 text-red-300')}>Over the SLA <span className="font-mono">{summary.data?.overdue}</span></button>}
        {byNext.map(([k, n]) => (
          <button key={k} onClick={() => setParams({ next: k })} className={cn('text-xs px-3 py-1.5 min-h-[36px] rounded-full border transition', next === k ? 'border-accent-400 bg-accent-600/20 text-accent-400' : 'border-slate-800 text-muted hover:text-muted-strong')}>
            {labels[k] ?? k} <span className="font-mono">{n}</span>
          </button>
        ))}
      </div>
      {wide && (
        <Card className="mb-4">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 items-end">
            <Select label="Clinic" value={clientId} onChange={setClientId} searchable options={[{ value: '', label: 'All clinics' }, ...(clients.data ?? []).map((c) => ({ value: c.id, label: c.name }))]} />
            <Select label="Doctor" value={doctor} onChange={setDoctor} searchable options={[{ value: '', label: 'All doctors' }, ...doctors.map((n) => ({ value: n, label: n }))]} />
            <label className="inline-flex items-center gap-2 text-xs text-muted min-h-[36px]"><input type="checkbox" className="accent-accent-400" checked={showDone} onChange={(e) => setShowDone(e.target.checked)} /> Include posted creatives</label>
          </div>
        </Card>
      )}

      {rows.isLoading && <div className="text-muted text-sm">Loading...</div>}
      {!rows.isLoading && groups.length === 0 && <Card><div className="text-sm text-muted">Nothing open.</div></Card>}
      {groups.map(([k, list]) => (
        <div key={k} className="mb-5">
          <div className="flex items-baseline gap-2 mb-2">
            <h2 className="text-xs uppercase tracking-wide text-muted inline-flex items-center gap-1">{list[0].next?.blocked && <AlertTriangle size={11} className="text-red-300" />} {labels[k] ?? k}</h2>
            <span className="text-[11px] text-muted/70">{list.length}</span>
          </div>
          <div className="space-y-2">
            {list.map((r) => (
              <Link key={r.creative_id} to={`/creatives/${r.creative_id}`} className="block">
                <Card className="hover:border-accent-400/40 transition">
                  <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
                    <div className="min-w-0 sm:w-[46%]">
                      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                        <span className="font-mono text-xs text-accent-400 whitespace-nowrap">{r.code}</span>
                        <span className="text-sm text-muted-strong truncate">{r.title}</span>
                        <span className="text-[10px] uppercase text-muted">{r.creative_type}</span>
                      </div>
                      <div className="text-xs text-muted truncate">{r.client_name}{r.talent_name ? ` · ${r.talent_name}` : ''}{r.shoot_at ? ` · shoot ${new Date(r.shoot_at).toLocaleDateString('en-AE', { day: 'numeric', month: 'short' })}` : ''}</div>
                    </div>
                    <div className="min-w-0 flex-1"><TrackBar row={r} compact /></div>
                  </div>
                </Card>
              </Link>
            ))}
          </div>
        </div>
      ))}
      <div className="text-[11px] text-muted mt-2 inline-flex items-center gap-1"><ListChecks size={11} /> Computed from what actually happened: script, doctor approval, shoot, raws, the edit phase by phase, reviews, client sign-off, posting. Nothing to update by hand.</div>
    </div>
  );
}

function Tile({ label, value, hint, tone, icon }: { label: string; value: number; hint?: string; tone?: 'red' | 'amber'; icon?: React.ReactNode }) {
  return (
    <Card>
      <div className="text-[10px] uppercase tracking-wide text-muted inline-flex items-center gap-1">{icon}{label}</div>
      <div className={cn('text-2xl font-semibold tabular-nums', tone === 'red' ? 'text-red-300' : tone === 'amber' ? 'text-amber-300' : 'text-muted-strong')}>{value}</div>
      {hint && <div className="text-[11px] text-muted">{hint}</div>}
    </Card>
  );
}
